import { useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { getAdminApiUrl, getEnvHeaders, Env } from './index';

/**
 * Thin authenticated fetch helper for the admin API. Mirrors the token +
 * per-env base-URL + WAF-dev-key pattern used across the admin site (see the
 * deals `api/` hooks) but centralizes it so each hook doesn't re-implement the
 * boilerplate.
 *
 * Routes are reached as `${getAdminApiUrl(env)}/admin/...` — the dev base URL
 * already carries the `/dev` stage, so the same path string resolves to
 * `/dev/admin/...` on dev and `/admin/...` on prod.
 *
 * Lives in `Admin/shared` because both the gamification tab (SSA-818) and the
 * referral-codes tab (SSA-955) talk to the same API the same way.
 */

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface AdminApiRequest {
  env: Env;
  path: string; // e.g. "/admin/referrals/codes"
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Serialized with `String(v)`; `undefined`, `null` and `''` are dropped.
   *  `boolean` is included because several admin endpoints take literal
   *  `true`/`false` query flags (e.g. the referrals list's `never_visited`),
   *  and the caller shouldn't have to stringify them just to satisfy a type. */
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
}

export function useAdminApi() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const request = useCallback(
    async <T = unknown>({ env, path, method = 'GET', query, body }: AdminApiRequest): Promise<T> => {
      const base = getAdminApiUrl(env);
      if (!base) {
        throw new ApiError(`API URL is not configured for ${env}`, 0);
      }

      let token: string | null = null;
      if (isAuthenticated && accounts.length > 0) {
        token = await acquireApiToken(instance, accounts[0]);
      }

      const qs = new URLSearchParams();
      if (query) {
        for (const [k, v] of Object.entries(query)) {
          if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
        }
      }
      const url = `${base}${path}${qs.toString() ? `?${qs}` : ''}`;

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getEnvHeaders(env),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });

      const text = await resp.text();
      const data = text ? JSON.parse(text) : {};
      if (!resp.ok) {
        // Endpoints that send a human-readable `message` alongside the machine
        // `error` code (the user-report queue, SSA-1578) get that message shown;
        // every other endpoint sends only `error`, which stays the message.
        throw new ApiError(
          data?.message || data?.error || `HTTP ${resp.status}`,
          resp.status,
          data?.error,
        );
      }
      return data as T;
    },
    [isAuthenticated, instance, accounts],
  );

  return { request };
}
